import { useLayoutEffect, useSyncExternalStore } from 'react';
import { getCurrentElakayRole } from '../pageInside/Rolle';

// The ORIGINAL (historical) Elakay Header/Footer. Its stored id stays 'legacy'
// for backward compatibility with the AnimationSettings / User documents.
// The ORIGINAL renders WITHOUT any data-hf-collection attribute, so no rule
// of HeaderFooterCollections.css can ever reach it.
export const ORIGINAL_COLLECTION_ID = 'legacy';
export const LEGACY_COLLECTION_ID = ORIGINAL_COLLECTION_ID;
// Personal preference values: 'platform' follows the platform design (a
// personal 'legacy' too: older servers stored it as every user's default);
// 'original' picks the ORIGINAL explicitly; or one of the five collections.
export const PLATFORM_CHOICE_ID = 'platform';
export const PERSONAL_ORIGINAL_ID = 'original';

export const HEADER_FOOTER_COLLECTIONS = Object.freeze([
  { id: 'classic-elegant', name: 'Classic Elegant', descriptionKey: 'h_hfc_desc_classic', description: 'Deep navy and champagne gold: refined, institutional and timeless.', palette: ['#0f1e33', '#c9a96e', '#f7f3ea'] },
  { id: 'marketplace-pro', name: 'Marketplace Pro', descriptionKey: 'h_hfc_desc_marketplace', description: 'Bright commerce header with search first, orange buying accents and a slate footer.', palette: ['#ffffff', '#f97316', '#0f172a'] },
  { id: 'modern-compact', name: 'Modern Compact', descriptionKey: 'h_hfc_desc_compact', description: 'Flat graphite surfaces, violet accents and a tight, efficient rhythm.', palette: ['#111113', '#8b5cf6', '#f4f4f5'] },
  { id: 'mega-navigation', name: 'Mega Navigation', descriptionKey: 'h_hfc_desc_mega', description: 'Royal blue with an amber navigation rail and a footer of clear cards.', palette: ['#172554', '#f59e0b', '#dbeafe'] },
  { id: 'dynamic-experience', name: 'Dynamic Experience', descriptionKey: 'h_hfc_desc_dynamic', description: 'Expressive gradients, glass surfaces and magenta / cyan light.', palette: ['#0b1023', '#d946ef', '#22d3ee'] },
]);

export const ORIGINAL_DESIGN = Object.freeze({
  id: ORIGINAL_COLLECTION_ID,
  name: 'Original Elakay',
  nameKey: 'h_hfc_original_name',
  descriptionKey: 'h_hfc_desc_original',
  description: 'The historical Elakay Header and Footer, exactly as they were.',
  palette: ['#004b63', '#007399', '#4facfe'],
  original: true,
});

// ORIGINAL first, then the five alternative collections (Previous / Next order).
export const DESIGN_CHOICES = Object.freeze([ORIGINAL_DESIGN, ...HEADER_FOOTER_COLLECTIONS]);

export const isCollectionId = id => HEADER_FOOTER_COLLECTIONS.some(collection => collection.id === id);
export const isDesignId = id => id === ORIGINAL_COLLECTION_ID || isCollectionId(id);

export const getCollection = id => HEADER_FOOTER_COLLECTIONS.find(collection => collection.id === id) || null;
export const getDesign = id => DESIGN_CHOICES.find(design => design.id === id) || ORIGINAL_DESIGN;

// Personal value -> design id (null = follow the platform), and back.
export const personalToDesign = (value) => {
  if (value === PERSONAL_ORIGINAL_ID) return ORIGINAL_COLLECTION_ID;
  return isCollectionId(value) ? value : null;
};
export const designToPersonal = designId => (designId === ORIGINAL_COLLECTION_ID ? PERSONAL_ORIGINAL_ID : designId);

// Priority: Family preview (nothing saved) > Use Original Design (ON by
// default) > personal choice (when World Family allows it; World Family
// itself always) > platform default > ORIGINAL. Anything unknown falls back
// to the ORIGINAL.
export const resolveHeaderFooterCollection = ({
  useOriginalDesign = true,
  globalCollectionId,
  userCollectionId,
  allowUserCustomization = false,
  isFamily = false,
  previewId = null,
} = {}) => {
  if (isDesignId(previewId)) return previewId;
  if (useOriginalDesign !== false) return ORIGINAL_COLLECTION_ID;
  const personal = personalToDesign(userCollectionId);
  if ((allowUserCustomization === true || isFamily) && personal) return personal;
  if (isCollectionId(globalCollectionId)) return globalCollectionId;
  return ORIGINAL_COLLECTION_ID;
};

// What visitors without a personal choice see.
export const resolvePlatformDesign = (config = {}) =>
  resolveHeaderFooterCollection({ useOriginalDesign: config?.useOriginalDesign, globalCollectionId: config?.collectionId });

// The attribute is only set for an alternative collection.
export const collectionAttribute = id => (isCollectionId(id) ? id : undefined);

// ── Preview without applying (World Family) ────────────────────────────────
// Lives in this tab only (sessionStorage): nothing is saved, other visitors
// never see it. Inside the Animation Settings device preview frame it comes
// from the frame URL / postMessage and never touches the tab's storage.
export const PREVIEW_STORAGE_KEY = 'elakay.hfPreview';
export const PREVIEW_MESSAGE = 'elakay:hf-preview';
export const PREVIEW_SCROLL_MESSAGE = 'elakay:hf-preview-scroll';

export const isPreviewFrame = () => {
  if (typeof window === 'undefined') return false;
  try { return window.self !== window.top; } catch (error) { return true; }
};

const readInitialPreview = () => {
  if (typeof window === 'undefined') return null;
  try {
    if (isPreviewFrame()) {
      const fromUrl = new URLSearchParams(window.location.search).get('hfPreview');
      if (isDesignId(fromUrl)) return fromUrl;
    }
    const stored = window.sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    return isDesignId(stored) ? stored : null;
  } catch (error) {
    return null;
  }
};

let previewId = readInitialPreview();
const previewListeners = new Set();

export const getHeaderFooterPreview = () => previewId;

export const setHeaderFooterPreview = (id) => {
  const next = isDesignId(id) ? id : null;
  if (next === previewId) return;
  previewId = next;
  if (!isPreviewFrame()) {
    try {
      if (next) window.sessionStorage.setItem(PREVIEW_STORAGE_KEY, next);
      else window.sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
    } catch (error) { /* private mode: the preview simply stays in memory */ }
  }
  previewListeners.forEach(listener => listener());
};

const subscribePreview = (listener) => {
  previewListeners.add(listener);
  return () => previewListeners.delete(listener);
};

export const useHeaderFooterPreview = () =>
  useSyncExternalStore(subscribePreview, getHeaderFooterPreview, () => null);

if (typeof window !== 'undefined' && isPreviewFrame()) {
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === PREVIEW_MESSAGE) setHeaderFooterPreview(event.data.id);
    if (event.data?.type === PREVIEW_SCROLL_MESSAGE) {
      const target = event.data.target === 'footer' ? 'footer.navFooter' : 'header.header';
      document.querySelector(target)?.scrollIntoView({ block: 'start' });
    }
  });
}

// The design this viewer gets, from the platform settings, the viewer's own
// choice and a running Family preview.
export const useHeaderFooterCollection = (infoSource) => {
  const preview = useHeaderFooterPreview();
  const config = infoSource?.allSettings?.headerFooterCollections || {};
  const isFamily = getCurrentElakayRole(infoSource) === 'Family';
  return resolveHeaderFooterCollection({
    useOriginalDesign: config.useOriginalDesign,
    globalCollectionId: config.collectionId,
    allowUserCustomization: config.allowUserCustomization === true,
    userCollectionId: infoSource?.userInfo?.appearance?.headerFooterCollectionId,
    isFamily,
    previewId: isFamily ? preview : null,
  });
};

// ── Navigation sync (menus, drawers, filters, dropdowns) ───────────────────
// The active collection is mirrored on <html>, so the navigation that lives
// outside <header>/<footer> (side drawers, filter panels, portalled dropdowns)
// reads the same --theme-* tokens (headerFooterThemeTokens.css). Like the
// Header/Footer attribute, it is never set for the ORIGINAL design.
export const THEME_ATTRIBUTE = 'data-hf-theme';

export const applyHeaderFooterTheme = (id, root = typeof document !== 'undefined' ? document.documentElement : null) => {
  if (!root) return;
  const value = collectionAttribute(id);
  if (value) root.setAttribute(THEME_ATTRIBUTE, value);
  else root.removeAttribute(THEME_ATTRIBUTE);
};

// Mounted once (Routes). Layout effect: <html> changes in the same frame as the
// Header/Footer attribute, so a switch or preview never shows mixed designs.
export const useHeaderFooterThemeSync = (infoSource) => {
  const design = useHeaderFooterCollection(infoSource);
  useLayoutEffect(() => {
    applyHeaderFooterTheme(design);
  }, [design]);
  useLayoutEffect(() => () => applyHeaderFooterTheme(null), []);
  return design;
};
